import React from 'react';
import { INPUT_TYPES } from '../../utils/constants';
import { useTheme } from '../../theme';

// Remove any leftover CSS from previous implementation
if (typeof document !== 'undefined') {
  const oldStyles = document.getElementById('signed-number-styles');
  if (oldStyles) {
    oldStyles.remove();
  }
}

interface InputProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: keyof typeof INPUT_TYPES;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  title?: string;
  id?: string;
}

const Input: React.FC<InputProps> = ({
  value,
  onChange,
  placeholder,
  type = 'TEXT',
  label,
  disabled = false,
  readOnly = false,
  className = '',
  min,
  max,
  title,
  id,
}) => {
  // Generate unique ID for label association if not provided
  const inputId = React.useId();
  const effectiveId = id || `input-${inputId}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || !onChange) return;
    
    const newValue = e.target.value;
    
    if (type === 'SIGNED_NUMBER') {
      // If it's a valid number, format it with sign
      const numericValue = parseFloat(newValue);
      if (!isNaN(numericValue)) {
        const formattedValue = numericValue >= 0 ? `+${numericValue}` : `${numericValue}`;
        onChange(formattedValue);
      } else {
        onChange(newValue); // Allow partial input like "-" or "+"
      }
    } else {
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    if (!onChange) return;

    if (type === 'SIGNED_NUMBER') {
      const currentValue = parseFloat(value.toString().replace(/^[+-]/, '')) || 0;
      const isNegative = value.toString().startsWith('-');
      const actualValue = isNegative ? -currentValue : currentValue;
      const newValue = actualValue + 1;
      const formattedValue = newValue >= 0 ? `+${newValue}` : `${newValue}`;
      onChange(formattedValue);
    } else if (type === 'NUMBER') {
      const currentValue = parseInt(value.toString()) || 0;
      const newValue = currentValue + 1;
      onChange(newValue.toString());
    }
  };

  const handleDecrement = () => {
    if (!onChange) return;

    if (type === 'SIGNED_NUMBER') {
      const currentValue = parseFloat(value.toString().replace(/^[+-]/, '')) || 0;
      const isNegative = value.toString().startsWith('-');
      const actualValue = isNegative ? -currentValue : currentValue;
      const newValue = actualValue - 1;
      const formattedValue = newValue >= 0 ? `+${newValue}` : `${newValue}`;
      onChange(formattedValue);
    } else if (type === 'NUMBER') {
      const currentValue = parseInt(value.toString()) || 0;
      const newValue = currentValue - 1;
      if (newValue >= 0) {
        onChange(newValue.toString());
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'SIGNED_NUMBER' || type === 'NUMBER') {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleIncrement();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleDecrement();
      }
    }
  };

  const getInputType = () => {
    if (type === 'SIGNED_NUMBER') return 'text';
    return INPUT_TYPES[type];
  };

  const inputClasses = [
    'input',
    disabled && 'input--disabled',
    readOnly && 'input--readonly',
    className,
  ].filter(Boolean).join(' ');

  if (type === 'SIGNED_NUMBER') {
    return (
      <div className="input-group">
        {label && (
          <label className="input-label" htmlFor={effectiveId}>
            {label}
          </label>
        )}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <input
            id={effectiveId}
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={type === 'SIGNED_NUMBER' && placeholder ?
              (parseFloat(placeholder) >= 0 ? `+${placeholder}` : placeholder) :
              placeholder}
            disabled={disabled}
            readOnly={readOnly}
            className={inputClasses}
            style={{ paddingRight: readOnly ? '0' : 'var(--semantic-spacing-component-xl)' }}
            title={title}
          />
          {!readOnly && (
            <div style={{ 
              position: 'absolute', 
              right: 'var(--semantic-spacing-component-xs)', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--semantic-spacing-component-xs)'
            }}>
            <button
              type="button"
              onClick={handleIncrement}
              disabled={disabled}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 'var(--semantic-spacing-component-md)',
                lineHeight: 'var(--semantic-spacing-component-md)',
                padding: 'var(--semantic-spacing-component-xs)',
                width: 'var(--semantic-spacing-component-lg)',
                height: 'var(--semantic-spacing-component-md)',
                color: 'var(--semantic-colors-text-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ▲
            </button>
            <button
              type="button"
              onClick={handleDecrement}
              disabled={disabled}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 'var(--semantic-spacing-component-md)',
                lineHeight: 'var(--semantic-spacing-component-md)',
                padding: 'var(--semantic-spacing-component-xs)',
                width: 'var(--semantic-spacing-component-lg)',
                height: 'var(--semantic-spacing-component-md)',
                color: 'var(--semantic-colors-text-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ▼
            </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="input-group">
      {label && (
        <label className="input-label" htmlFor={effectiveId}>
          {label}
        </label>
      )}
      <input
        id={effectiveId}
        type={getInputType()}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={inputClasses}
        min={min}
        max={max}
        title={title}
      />
    </div>
  );
};

export default Input;